import {
  Modal,
  Stack,
  Box,
  Typography,
  IconButton,
  TextField,
  Alert,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { desktopSx } from "src/utils/styles/modalStyles";
import { FormEvent, useCallback, useState } from "react";
import { AbTestVariantName } from "src/api/types";
import LoadingButton from "src/components/Buttons/LoadingButton";
import { useCreateVariantMutation } from "src/api/services/experimentsApi";
import { useSentryLogger } from "src/hooks/useSentryLogger";

const names: AbTestVariantName[] = ["Variant A", "Variant B", "Variant C"];

interface Props {
  experimentId?: string;
  open: boolean;
  onClose: () => void;
}

const CreateNewVariantModal = ({ onClose, open, experimentId }: Props) => {
  const logError = useSentryLogger("error");
  const [createVariant, { isLoading, error: submitError }] =
    useCreateVariantMutation();
  const [error, setError] = useState("");
  const [name, setName] = useState<AbTestVariantName | string>("");
  const [description, setDescription] = useState("");

  const handleName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setName(event.target.value);
      setError("");
    },
    [],
  );

  const handleDescription = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setDescription(event.target.value);
      setError("");
    },
    [],
  );

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (experimentId == null) {
        setError("Experiment ID is required");
        return;
      }

      try {
        await createVariant({
          experiment_id: Number(experimentId),
          name,
          description,
        });
        setName("");
        setDescription("");
        onClose();
      } catch (e) {
        setError("Failed to create variant");
        logError("Failed to create variant", e);
      }
    },
    [createVariant, description, experimentId, logError, name, onClose],
  );

  return (
    <Modal open={open}>
      <Stack
        sx={desktopSx}
        gap={2}
        component="form"
        maxWidth={400}
        onSubmit={handleSubmit}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="h5" fontWeight={700}>
            Add Variant
          </Typography>
          <IconButton onClick={onClose} sx={{ p: 0 }}>
            <CloseIcon fontSize="medium" />
          </IconButton>
        </Box>
        <Stack gap={2}>
          <TextField
            select
            label="Variant Name"
            required
            value={name}
            onChange={handleName}
          >
            {names.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Description"
            required
            value={description}
            onChange={handleDescription}
          />

          <LoadingButton type="submit" loading={isLoading}>
            Create variant
          </LoadingButton>
        </Stack>
        {error ? <Alert severity={"error"}>{error}</Alert> : null}
        {submitError ? (
          <Alert severity={"error"}>{JSON.stringify(submitError)}</Alert>
        ) : null}
      </Stack>
    </Modal>
  );
};

export default CreateNewVariantModal;
